/**
 * Save as State Component
 * 
 * Shown after first session completion.
 * "This is now yours"
 * 
 * Allows user to save their created session as a reusable state.
 */

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, Check, X } from 'lucide-react';

// Default state name suggestions based on desired feeling
const STATE_SUGGESTIONS = {
  calm: 'My Calm State',
  focus: 'My Focus State',
  confidence: 'My Confidence State',
  energy: 'My Energy State',
  presence: 'My Presence State',
  clarity: 'My Clarity State',
  // Fallbacks for older state IDs
  inner_peace: 'My Calm State',
  mental_clarity: 'My Focus State',
  emotional_balance: 'My Balance State'
};

const SaveAsState = ({ 
  desiredState,
  sessionConfig,
  onSave,
  onSkip,
  onClose
}) => {
  const [phase, setPhase] = useState('prompt'); // prompt, naming, success
  const [stateName, setStateName] = useState(STATE_SUGGESTIONS[desiredState] || 'My State');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave({
        name: stateName,
        desiredState,
        config: sessionConfig
      });
      setPhase('success');
    } catch (err) {
      console.error('Error saving state:', err);
      setSaving(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-[#0a0a12]/95 z-50 flex items-center justify-center p-6"
    >
      {/* Close button */}
      <button
        onClick={onClose || onSkip}
        className="absolute top-6 right-6 text-white/30 hover:text-white/60 transition-colors"
      >
        <X size={24} />
      </button>

      <AnimatePresence mode="wait">
        {/* PHASE 1: Prompt */}
        {phase === 'prompt' && (
          <motion.div
            key="prompt"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-sm"
          >
            <motion.h2
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="text-white text-2xl font-light mb-4"
            >
              Save this as a state?
            </motion.h2>
            
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
              className="text-white/40 text-sm mb-10"
            >
              You can return to this anytime
            </motion.p>
            
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
              className="space-y-4"
            >
              <button
                onClick={() => setPhase('naming')}
                className="w-full py-4 px-6 rounded-2xl bg-violet-500/20 border border-violet-500/40
                         text-white font-medium hover:bg-violet-500/30 transition-all
                         flex items-center justify-center gap-2"
                data-testid="save-state-yes"
              >
                Save
                <ArrowRight size={18} />
              </button>
              
              <button
                onClick={onSkip}
                className="w-full py-3 text-white/40 hover:text-white/60 transition-colors text-sm"
                data-testid="save-state-skip"
              >
                Not now
              </button>
            </motion.div>
          </motion.div>
        )}

        {/* PHASE 2: Naming */}
        {phase === 'naming' && (
          <motion.div
            key="naming"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-sm w-full"
          >
            <motion.h2
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="text-white text-2xl font-light mb-8"
            >
              Name your state
            </motion.h2>
            
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
              className="mb-8"
            >
              <input
                type="text"
                value={stateName}
                onChange={(e) => setStateName(e.target.value)}
                className="w-full px-6 py-4 rounded-2xl bg-white/5 border border-white/20
                         text-white text-center text-lg focus:outline-none focus:border-violet-500/50
                         transition-colors"
                placeholder="My State"
                autoFocus
                data-testid="state-name-input"
              />
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
              className="flex gap-3"
            >
              <button
                onClick={() => setPhase('prompt')}
                className="flex-1 py-4 rounded-2xl bg-white/5 border border-white/10
                         text-white/60 hover:bg-white/10 transition-all"
              >
                Back
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !stateName.trim()}
                className="flex-1 py-4 rounded-2xl bg-violet-500 text-white font-medium
                         hover:bg-violet-600 transition-all disabled:opacity-50
                         flex items-center justify-center gap-2"
                data-testid="confirm-save-state"
              >
                {saving ? 'Saving...' : 'Save'}
                {!saving && <Check size={18} />}
              </button>
            </motion.div>
          </motion.div>
        )}

        {/* PHASE 3: Success */}
        {phase === 'success' && (
          <motion.div
            key="success"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-sm"
          >
            {/* Success icon */}
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: 'spring' }}
              className="w-20 h-20 mx-auto mb-8 rounded-full bg-violet-500/20 border border-violet-500/40
                       flex items-center justify-center"
            >
              <Check size={32} className="text-violet-400" />
            </motion.div>
            
            <motion.h2
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
              className="text-white text-2xl font-light mb-3"
            >
              This is now yours
            </motion.h2>
            
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
              className="space-y-2 mb-10"
            >
              <p className="text-white/40 text-sm">You don't need to start again</p>
              <p className="text-white/40 text-sm">just return</p>
            </motion.div>
            
            <motion.button
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.8 }}
              onClick={onClose}
              className="px-8 py-4 rounded-full bg-white/10 border border-white/20
                       text-white hover:bg-white/15 transition-all"
              data-testid="state-saved-done"
            >
              Continue
            </motion.button>
            
            {/* Excelior whisper */}
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.2 }}
              className="text-white/20 text-xs italic mt-8"
            >
              "You're already doing it right"
            </motion.p>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default SaveAsState;
